import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface CalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

const CALC_WIDTH = 300;
const BUTTON_GAP = 8;
const EDGE_MARGIN = 8;

export default function Calculator({ isOpen, onClose }: CalculatorProps) {
  const [display, setDisplay] = useState("0");
  const [expression, setExpression] = useState("0");
  const [expressionBase, setExpressionBase] = useState("");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(
    null,
  );
  const anchorRef = useRef<{ top: number; left: number } | null>(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, offsetX: 0, offsetY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    anchorRef.current = anchor;
  }, [anchor]);

  useEffect(() => {
    if (!isOpen) {
      setDisplay("0");
      setExpression("0");
      setExpressionBase("");
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(false);
      setDragOffset({ x: 0, y: 0 });
      setAnchor(null);
      return;
    }

    const btn = document.querySelector<HTMLElement>("[data-calculator-button]");
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const desiredLeft = rect.left + rect.width / 2 - CALC_WIDTH / 2;
    const clampedLeft = Math.max(
      EDGE_MARGIN,
      Math.min(desiredLeft, window.innerWidth - CALC_WIDTH - EDGE_MARGIN),
    );
    setAnchor({ top: rect.top, left: clampedLeft });
  }, [isOpen]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const newX =
        dragStartRef.current.offsetX +
        (e.clientX - dragStartRef.current.mouseX);
      const newY =
        dragStartRef.current.offsetY +
        (e.clientY - dragStartRef.current.mouseY);
      const height = containerRef.current?.offsetHeight ?? 0;
      const currentAnchor = anchorRef.current;
      const minY = currentAnchor
        ? EDGE_MARGIN - currentAnchor.top + height + BUTTON_GAP
        : -(window.innerHeight / 2) + height / 2;
      setDragOffset({ x: newX, y: Math.max(minY, newY) });
    };
    const handleMouseUp = () => {
      draggingRef.current = false;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      offsetX: dragOffset.x,
      offsetY: dragOffset.y,
    };
  };

  const roundResult = (num: number): number => {
    return Math.round(num * 1e10) / 1e10;
  };

  const isErrorDisplay = display === "Error";

  const handleNumber = (num: string) => {
    if (isErrorDisplay) {
      setDisplay(num);
      setExpressionBase("");
      setExpression(num);
      setWaitingForNewValue(false);
    } else if (waitingForNewValue) {
      setDisplay(num);
      setExpression(expressionBase + num);
      setWaitingForNewValue(false);
    } else {
      const newDisplay = display === "0" ? num : display + num;
      setDisplay(newDisplay);
      setExpression(expressionBase + newDisplay);
    }
  };

  const handleDecimal = () => {
    if (isErrorDisplay) {
      setDisplay("0.");
      setExpressionBase("");
      setExpression("0.");
      setWaitingForNewValue(false);
    } else if (waitingForNewValue) {
      setDisplay("0.");
      setExpression(expressionBase + "0.");
      setWaitingForNewValue(false);
    } else if (!display.includes(".")) {
      const newDisplay = display + ".";
      setDisplay(newDisplay);
      setExpression(expressionBase + newDisplay);
    }
  };

  const calculate = (prev: number, current: number, op: string): number => {
    switch (op) {
      case "+":
        return roundResult(prev + current);
      case "-":
        return roundResult(prev - current);
      case "×":
        return roundResult(prev * current);
      case "÷":
        return current === 0 ? NaN : roundResult(prev / current);
      default:
        return current;
    }
  };

  const handleOperation = (op: string) => {
    if (isErrorDisplay) return;
    const currentValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(currentValue);
      const newBase = display + op;
      setExpressionBase(newBase);
      setExpression(newBase);
    } else if (operation) {
      const result = calculate(previousValue, currentValue, operation);
      if (Number.isNaN(result)) {
        setDisplay("Error");
        setExpression("Error");
        setExpressionBase("");
        setPreviousValue(null);
        setOperation(null);
        setWaitingForNewValue(true);
        return;
      }
      setDisplay(String(result));
      setPreviousValue(result);
      const newBase = expression + op;
      setExpressionBase(newBase);
      setExpression(newBase);
    }

    setOperation(op);
    setWaitingForNewValue(true);
  };

  const handleEquals = () => {
    if (operation && previousValue !== null) {
      const currentValue = parseFloat(display);
      const result = calculate(previousValue, currentValue, operation);
      const resultStr = Number.isNaN(result) ? "Error" : String(result);
      setDisplay(resultStr);
      setExpression(resultStr);
      setExpressionBase("");
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(true);
    }
  };

  const handleClear = () => {
    setDisplay("0");
    setExpression("0");
    setExpressionBase("");
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
  };

  const handleBackspace = () => {
    if (isErrorDisplay || display.length === 1) {
      setDisplay("0");
      setExpression(expressionBase + "0");
    } else {
      const newDisplay = display.slice(0, -1);
      setDisplay(newDisplay);
      setExpression(expressionBase + newDisplay);
    }
  };

  const buttonClass =
    "w-full py-2.5 rounded bg-[#2C2C3A] hover:bg-[#262635] text-white font-medium transition-colors text-sm";
  const operationButtonClass =
    "w-full py-2.5 rounded bg-[#4CAF50] hover:bg-[#45a049] text-white font-medium transition-colors text-sm";

  if (!isOpen || !anchor) return null;

  return createPortal(
    <div style={{ position: "fixed", zIndex: 1000 }}>
      <div
        ref={containerRef}
        className="bg-[#070717] rounded-2xl shadow-2xl border border-white/10"
        style={{
          position: "fixed",
          bottom: `calc(100vh - ${anchor.top - BUTTON_GAP + dragOffset.y}px)`,
          left: `${anchor.left + dragOffset.x}px`,
          width: `${CALC_WIDTH}px`,
          maxWidth: "90vw",
          transform: "scale(0.85)",
          transformOrigin: "bottom center",
        }}
      >
        {/* Drag handle / header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b border-white/10 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleDragStart}
        >
          <span className="text-white text-sm font-semibold">Calculator</span>
          <button
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-3">
          {/* Display */}
          <div className="bg-[#2C2C3A] rounded p-2 mb-3 text-right">
            <div className="text-white text-2xl font-mono overflow-hidden min-h-[34px] flex items-center justify-end max-w-full truncate">
              {expression.length > 16 ? expression.slice(-16) : expression}
            </div>
          </div>

          {/* Buttons Grid */}
          <div className="grid grid-cols-4 gap-1.5">
            {/* Row 1 */}
            <button onClick={handleClear} className={buttonClass}>
              AC
            </button>
            <button onClick={handleBackspace} className={buttonClass}>
              ←
            </button>
            <button
              onClick={() => handleOperation("÷")}
              className={operationButtonClass}
            >
              ÷
            </button>
            <button
              onClick={() => handleOperation("×")}
              className={operationButtonClass}
            >
              ×
            </button>

            {/* Row 2 */}
            <button onClick={() => handleNumber("7")} className={buttonClass}>
              7
            </button>
            <button onClick={() => handleNumber("8")} className={buttonClass}>
              8
            </button>
            <button onClick={() => handleNumber("9")} className={buttonClass}>
              9
            </button>
            <button
              onClick={() => handleOperation("-")}
              className={operationButtonClass}
            >
              −
            </button>

            {/* Row 3 */}
            <button onClick={() => handleNumber("4")} className={buttonClass}>
              4
            </button>
            <button onClick={() => handleNumber("5")} className={buttonClass}>
              5
            </button>
            <button onClick={() => handleNumber("6")} className={buttonClass}>
              6
            </button>
            <button
              onClick={() => handleOperation("+")}
              className={operationButtonClass}
            >
              +
            </button>

            {/* Row 4 */}
            <button onClick={() => handleNumber("1")} className={buttonClass}>
              1
            </button>
            <button onClick={() => handleNumber("2")} className={buttonClass}>
              2
            </button>
            <button onClick={() => handleNumber("3")} className={buttonClass}>
              3
            </button>
            <button
              onClick={handleEquals}
              className="row-span-2 w-full py-2.5 rounded bg-[#FF9800] hover:bg-[#E58900] text-white font-medium transition-colors text-sm"
            >
              =
            </button>

            {/* Row 5 */}
            <button
              onClick={() => handleNumber("0")}
              className="col-span-2 w-full py-2.5 rounded bg-[#2C2C3A] hover:bg-[#262635] text-white font-medium transition-colors text-sm"
            >
              0
            </button>
            <button onClick={handleDecimal} className={buttonClass}>
              .
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
